import React, { useEffect, useState, useRef } from 'react'
import { Form, Modal, Button } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import {
    FormDropdownField,
    FormTextFormField,
    FormToggleFormField,
    SubmitCancelButtons,
} from 'elements'
import { FlexRowBetween, TitleText } from 'components'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { X } from 'phosphor-react'
import { useQueryString } from 'hooks'
import { FormErrorText } from 'utility'
import { InviteCustomerAssignModal } from './InviteCustomerAssignModal'
import { InviteTemplateAssignModal } from './InviteTemplateAssignModal'
import { GroupService } from 'utility/services/group'
import { TemplateAutoEmailSelector } from 'components/Modal'
import { PermissionSettingService } from 'utility/services/permission'
import { MenuSettingService } from 'utility/services/menu-setting'
import { useAuth } from 'context'
import styled from 'styled-components'

const ModalHeaderGradient = styled(Modal.Header)`
    background: linear-gradient(135deg, #3730A3 0%, #4F46E5 100%) !important;
    color: #ffffff !important;
    padding: 1.2rem 1.5rem !important;
    border-top-left-radius: 8px !important;
    border-top-right-radius: 8px !important;
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
`

const ModalTitleText = styled.span`
    font-size: 1.3rem;
    font-weight: 600;
    color: #ffffff;
`

const AccessModesContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-top: 8px;
    margin-bottom: 16px;
`

const AccessModeCard = styled.div`
    border: 2px solid ${props => props.active ? '#4F46E5' : '#E2E8F0'};
    background-color: ${props => props.active ? '#F5F3FF' : '#FFFFFF'};
    border-radius: 8px;
    padding: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    &:hover {
        border-color: #4F46E5;
    }
`

const AccessModeTitle = styled.div`
    font-weight: 600;
    font-size: 14px;
    color: ${props => props.active ? '#4F46E5' : '#0F172A'};
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
`

const AccessModeDesc = styled.div`
    font-size: 12px;
    color: #64748B;
    line-height: 1.4;
`

const MenuPreviewContainer = styled.div`
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background-color: #F8FAFC;
    margin-top: 12px;
    overflow: hidden;
`

const MenuPreviewHead = styled.div`
    background-color: #F1F5F9;
    padding: 10px 14px;
    font-weight: 600;
    font-size: 13px;
    color: #334155;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #E2E8F0;
`

const MenuPreviewBadge = styled.span`
    background-color: #EEF2FF;
    color: #4F46E5;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 9999px;
    font-weight: 500;
`

const MenuTreeContainer = styled.div`
    padding: 10px 14px;
    max-height: 240px;
    overflow-y: auto;
`

const MenuRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    font-size: 13px;
    border-bottom: 1px dashed #F1F5F9;
    &:last-child {
        border-bottom: none;
    }
    padding-left: ${props => props.indent ? '20px' : '0px'};
    color: ${props => props.denied ? '#94A3B8' : '#0F172A'};
`

const MenuStateLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    background-color: ${props => {
        if (props.state === 'Allow') return '#ECFDF5'
        if (props.state === 'Deny') return '#FEF2F2'
        return '#F1F5F9'
    }};
    color: ${props => {
        if (props.state === 'Allow') return '#10B981'
        if (props.state === 'Deny') return '#EF4444'
        return '#64748B'
    }};
`

const OverridesContainer = styled.div`
    margin-top: 16px;
    border-top: 1px dashed #CBD5E1;
    padding-top: 16px;
`

const OverrideRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #F1F5F9;
    &:last-child {
        border-bottom: none;
    }
`

const OverrideInfo = styled.div`
    display: flex;
    flex-direction: column;
`

const OverrideName = styled.span`
    font-weight: 500;
    font-size: 13px;
    color: #1E293B;
`

const OverrideKey = styled.span`
    font-size: 11px;
    color: #64748B;
    font-family: monospace;
`

const PillRadioContainer = styled.div`
    display: flex;
    border: 1px solid #CBD5E1;
    border-radius: 6px;
    overflow: hidden;
    background-color: #F8FAFC;
`

const PillButton = styled.button`
    border: none;
    background: ${props => {
        if (!props.active) return 'none'
        if (props.type === 'Allow') return '#10B981'
        if (props.type === 'Deny') return '#EF4444'
        return '#94A3B8'
    }};
    color: ${props => props.active ? '#FFFFFF' : '#475569'};
    font-size: 12px;
    font-weight: 500;
    padding: 6px 12px;
    cursor: pointer;
    transition: all 0.15s ease;
    outline: none;
    &:hover {
        background-color: ${props => props.active ? '' : '#E2E8F0'};
    }
`

const CustomerSchema = yup.object().shape({
    GroupId: yup
        .number()
        .required('Group is required')
        .nullable()
        .typeError('Group is required'),
    CustomerList: yup
        .array()
        .min(1, 'Minimum one Customer required')
        .nullable(),
    UserList: yup.array().min(1, 'Minimum one Team Member required').nullable(),
    TemplateList: yup
        .array()
        .min(1, 'Minimum one Templates required')
        .nullable(),
    OtherEmails: yup
        .string()
        .matches(
            /^$|^[^;]+(;[^;]+)*$/,
            'Enter values separated by semicolons (;) only',
        )
        .nullable(),
})

export const GroupAddEditModal = ({
    onSubmit,
    error,
    loading,
    groupOptions,
    groupDataList,
    customerOptions,
    templateOptions,
    userseOptions,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        clearErrors,
        formState: { errors, isSubmitted },
    } = useForm({
        resolver: yupResolver(CustomerSchema),
        mode: 'onSubmit',
        defaultValues: {
            GroupId: '',
            CustomerList: [],
            UserList: [],
            TemplateList: [],
            OtherEmails: '',
        },
    })
    const group = watch('GroupId')
    const params = useQueryString()
    const id = params.get('id')
    const justSubmittedRef = useRef(false)

    useEffect(() => {
        if (justSubmittedRef.current) {
            justSubmittedRef.current = false
            return
        }
        if (group) {
            const groupData = groupDataList.find(
                ele => ele.GroupId.toString() === group.toString(),
            )
            if (groupData) {
                reset(
                    {
                        GroupId: groupData.GroupId,
                        CustomerList:
                            groupData?.CustomerList?.split(',').map(ele =>
                                Number(ele),
                            ) || [],
                        UserList:
                            groupData?.UserList?.split(',').map(ele =>
                                Number(ele),
                            ) || [],
                        TemplateList:
                            groupData?.TemplateList?.split(',').map(ele =>
                                Number(ele),
                            ) || [],
                    },
                    {
                        keepErrors: false,
                        keepDirty: false,
                        keepIsSubmitted: false,
                        keepTouched: false,
                    },
                )
            }
        } else {
            reset(
                {
                    GroupId: '',
                    CustomerList: [],
                    UserList: [],
                    TemplateList: [],
                    OtherEmails: '',
                },
                {
                    keepErrors: false,
                    keepDirty: false,
                    keepIsSubmitted: false,
                    keepTouched: false,
                },
            )
        }
    }, [group])

    useEffect(() => {
        if (id) {
            setValue('GroupId', id)
        }
    }, [id])

    // State for Non User Emails (chips)
    const [nonUserEmails, setNonUserEmails] = useState([])
    // For Dropdown options, emails as { key, text, value }
    const emailOptions = nonUserEmails.map(email => ({
        key: email,
        text: email,
        value: email,
    }))

    // Handle Dropdown change
    const handleEmailsChange = (_, { value }) => {
        setNonUserEmails(value)
    }
    // Handle Dropdown addition
    const handleEmailsAdd = (_, { value }) => {
        if (value && !nonUserEmails.includes(value)) {
            setNonUserEmails([...nonUserEmails, value])
        }
    }

    // On submit, join emails for payload
    const submit = async formData => {
        await onSubmit({
            ...formData,
            UserList: formData.UserList.toString().replaceAll(';', ','),
            CustomerList: formData.CustomerList.toString().replaceAll(';', ','),
            TemplateList: formData.TemplateList.toString().replaceAll(';', ','),
            OtherEmails: nonUserEmails.join(';'),
        })
        setNonUserEmails([])
        justSubmittedRef.current = true
        // Clear errors first, then reset form state
        clearErrors()
        // Use setTimeout to ensure reset happens after current render cycle
        setTimeout(() => {
            reset(
                {
                    GroupId: '',
                    CustomerList: [],
                    UserList: [],
                    TemplateList: [],
                    OtherEmails: '',
                },
                {
                    keepErrors: false,
                    keepDirty: false,
                    keepIsSubmitted: false,
                    keepTouched: false,
                    keepIsValid: false,
                    keepSubmitCount: false,
                },
            )
        }, 0)
    }
    const onCancel = () => {
        setNonUserEmails([])
        reset(
            {
                GroupId: '',
                CustomerList: [],
                UserList: [],
                TemplateList: [],
                OtherEmails: '',
            },
            {
                keepErrors: false,
                keepDirty: false,
                keepIsSubmitted: false,
                keepTouched: false,
                keepIsValid: false,
                keepSubmitCount: false,
            },
        )
        onSubmit(false)
    }

    // Prepare template options with 'Select All' at the top
    const allTemplateValues = templateOptions?.map(opt => opt.value) || []
    const selectAllOption = {
        key: 'select_all',
        text: 'Select All',
        value: 'select_all',
    }
    const enhancedTemplateOptions = [
        selectAllOption,
        { key: 'divider', text: '──────────', value: '', disabled: true },
        ...(templateOptions || []),
    ]

    // Watch TemplateList for select all logic
    const templateListValue = watch('TemplateList') || []
    const handleTemplateChange = value => {
        if (Array.isArray(value)) {
            if (value.includes('select_all')) {
                // Select all templates
                setValue('TemplateList', allTemplateValues)
            } else {
                // Remove 'select_all' and '' (divider) from the value
                setValue(
                    'TemplateList',
                    value.filter(v => v !== 'select_all' && v !== ''),
                )
            }
        }
    }

    useEffect(() => {
        if (group) {
            const groupData = groupDataList.find(
                ele => ele.GroupId.toString() === group.toString(),
            )
            if (groupData) {
                setValue(
                    'TemplateList',
                    groupData.TemplateList
                        ? groupData.TemplateList.split(',').map(Number)
                        : [],
                )
                setNonUserEmails(
                    groupData.OtherEmails
                        ? groupData.OtherEmails.split(';').filter(Boolean)
                        : [],
                )
            }
        } else {
            setValue('TemplateList', [])
            setNonUserEmails([])
        }
    }, [group])

    return (
        <Form noValidate>
            <div className="my-2">
                {/* First row: Group, Customer, Team Member */}
                <Form.Group widths="equal">
                    <FormDropdownField
                        control={control}
                        label="Group"
                        name="GroupId"
                        placeholder="Group"
                        errors={isSubmitted ? errors?.GroupId : undefined}
                        options={groupOptions}
                        search
                        required
                    />
                    <FormDropdownField
                        control={control}
                        label="Customer"
                        name="CustomerList"
                        placeholder="Select Customers"
                        errors={isSubmitted ? errors?.CustomerList : undefined}
                        options={customerOptions}
                        defaultValue={[]}
                        search
                        multiple
                        required
                    />
                    <FormDropdownField
                        control={control}
                        label="Team Member"
                        name="UserList"
                        placeholder="Select TeamMembers"
                        errors={isSubmitted ? errors?.UserList : undefined}
                        options={userseOptions}
                        defaultValue={[]}
                        search
                        multiple
                        required
                    />
                </Form.Group>
                {/* Second row: Templates, OtherEmails */}
                <Form.Group widths="equal">
                    <FormDropdownField
                        control={control}
                        label="Templates"
                        name="TemplateList"
                        placeholder="Select Templates"
                        errors={isSubmitted ? errors?.TemplateList : undefined}
                        options={enhancedTemplateOptions}
                        defaultValue={[]}
                        search
                        multiple
                        onChange={(_, item) => handleTemplateChange(item.value)}
                        value={templateListValue}
                        required
                    />
                    <Form.Field>
                        <label className="required" style={{ fontWeight: 700 }}>
                            Non User Emails
                        </label>
                        <Dropdown
                            placeholder="Type email and press Enter."
                            fluid
                            multiple
                            search
                            selection
                            allowAdditions
                            value={nonUserEmails}
                            options={emailOptions}
                            onChange={handleEmailsChange}
                            onAddItem={handleEmailsAdd}
                            icon={null}
                            noResultsMessage={null}
                        />
                    </Form.Field>
                </Form.Group>
            </div>
            {error && <div className="error">{error}</div>}
            <SubmitCancelButtons
                loading={loading}
                onSubmit={handleSubmit(submit)}
                onCancel={onCancel}
            />
        </Form>
    )
}
export const GroupCapaAddEditModal = ({
    onSubmit,
    error,
    loading,
    groupOptions,
    groupDataList,
    templateOptions,
    // setCapa,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(CustomerSchema),
        // defaultValues: setCapa,
    })
    // console.log(setCapa)
    const group = watch('GroupId')
    const params = useQueryString()
    const id = params.get('id')
    // console.log(templateOptions,)
    useEffect(() => {
        if (group) {
            const groupData = groupDataList.find(
                ele => ele.GroupId.toString() === group.toString(),
            )
            console.log(group)
            if (groupData) {
                reset({
                    GroupId: groupData.GroupId,
                })
            }
        } else {
            reset({ GroupId: '' })
        }
    }, [group])

    useEffect(() => {
        if (id) {
            setValue('GroupId', id)
        }
    }, [id])

    const submit = async formData => {
        // console.log("before submit",formData)
        // eslint-disable-next-line prefer-destructuring
        // formData.templateId = formData.templateId[0]
        onSubmit({
            ...formData,
            // UserList: formData.UserList.toString().replaceAll(';', ','),
        })
        reset({ GroupId: '' })
    }
    // const submit = async formData => {
    //     console.log('before submit', formData)

    //     // Destructure the first element of TemplateList array if it exists
    //     const [templateId] = formData.TemplateList || []

    //     // Assign templateId to formData
    //     formData.templateId = templateId

    //     onSubmit({
    //         ...formData,
    //         // UserList: formData.UserList.toString().replaceAll(';', ','),
    //     })

    //     reset({ GroupId: '' })
    // }

    const onCancel = () => {
        reset({ GroupId: '' })
        onSubmit(false)
    }
    console.log(groupDataList)
    return (
        <Form noValidate>
            <div className="my-2">
                <Form.Group widths="equal">
                    <FormDropdownField
                        control={control}
                        label="Group"
                        name="GroupId"
                        placeholder="Group"
                        errors={errors?.GroupId}
                        options={groupOptions}
                        search
                        required
                    />
                </Form.Group>
                <FormDropdownField
                    control={control}
                    label="Templates"
                    name="TemplateList"
                    placeholder="Select Templates"
                    errors={errors?.TemplateList}
                    options={templateOptions}
                    defaultValue={[]}
                    search
                    multiple
                    required
                />
            </div>
            {error && <div className="error">{error}</div>}
            <SubmitCancelButtons
                loading={loading}
                onSubmit={handleSubmit(submit)}
                onCancel={onCancel}
            />
        </Form>
    )
}

const GroupSchema = yup.object().shape({
    GroupName: yup
        .string()
        .trim(FormErrorText.SpacesNotAllowed)
        .strict()
        .required('Group Name is required'),
    IsCapa: yup.boolean(),
})

export const AddGroupModal = ({ open, onClose, loading, data }) => {
    const {
        control,
        handleSubmit,
        reset,
        // watch,
        // setValue,
        formState: { errors },
    } = useForm({ resolver: yupResolver(GroupSchema) })
    const [autoEmail, setAutoEmail] = useState(data?.AutoEmail || false)
    const [selectedTemplates, setSelectedTemplates] = useState(
        data?.TemplateId || [],
    )

    useEffect(() => {
        if (data) {
            reset({ ...data })
            setAutoEmail(data.AutoEmail || false)
            setSelectedTemplates(data.TemplateId || [])
        } else {
            reset({ IsCapa: false })
            setAutoEmail(false)
            setSelectedTemplates([])
        }
    }, [open])

    const onSubmit = formData => {
        onClose({
            ...formData,
            AutoEmail: autoEmail,
            TemplateId: autoEmail ? selectedTemplates : null,
        })
    }

    return (
        <Modal
            open={open}
            onCancel={() => onClose()}
            className="model"
            size="small"
        >
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>
                        {!data.GroupName ? 'Create Group' : 'Rename Group'}
                    </TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form noValidate>
                    <Form.Field>
                        <FormTextFormField
                            control={control}
                            label="Group Name"
                            name="GroupName"
                            placeholder="Group Name"
                            errors={errors?.GroupName}
                            defaultValue=""
                            required
                        />
                    </Form.Field>
                    {/* <Form.Field>
                        <Checkbox
                            label="Is CAPA Group"
                            checked={watch('IsCapa')}
                            onChange={(e, { checked }) =>
                                setValue('IsCapa', checked)
                            }
                        />
                    </Form.Field> */}
                    <TemplateAutoEmailSelector
                        autoEmail={autoEmail}
                        setAutoEmail={setAutoEmail}
                        selectedTemplates={selectedTemplates}
                        setSelectedTemplates={setSelectedTemplates}
                    />
                    <SubmitCancelButtons
                        onSubmit={handleSubmit(onSubmit)}
                        onCancel={onClose}
                        loading={loading}
                    />
                </Form>
            </Modal.Content>
        </Modal>
    )
}

export const AddCapaGroupModal = ({ open, onClose, loading, data }) => {
    const {
        control,
        handleSubmit,
        reset,
        // watch,
        // setValue,
        formState: { errors },
    } = useForm({ resolver: yupResolver(GroupSchema) })
    const [autoEmail, setAutoEmail] = useState(data?.AutoEmail || false)
    const [selectedTemplates, setSelectedTemplates] = useState(
        data?.TemplateId || [],
    )

    useEffect(() => {
        if (data) {
            reset({ ...data })
            setAutoEmail(data.AutoEmail || false)
            setSelectedTemplates(data.TemplateId || [])
        } else {
            reset({ IsCapa: false })
            setAutoEmail(false)
            setSelectedTemplates([])
        }
    }, [open])

    const onSubmit = formData => {
        onClose({
            ...formData,
            AutoEmail: autoEmail,
            TemplateId: autoEmail ? selectedTemplates : null,
        })
    }

    return (
        <Modal
            open={open}
            onCancel={() => onClose()}
            className="model"
            size="small"
        >
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>
                        {!data.GroupName ? 'Create Group' : 'Rename Group'}
                    </TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form noValidate>
                    <Form.Field>
                        <FormTextFormField
                            control={control}
                            label="Group Name"
                            name="GroupName"
                            placeholder="Group Name"
                            errors={errors?.GroupName}
                            defaultValue=""
                            required
                        />
                    </Form.Field>
                    {/* <Form.Field>
                        <Checkbox
                            label="Is CAPA Group"
                            checked={watch('IsCapa')}
                            onChange={(e, { checked }) =>
                                setValue('IsCapa', checked)
                            }
                        />
                    </Form.Field> */}
                    <TemplateAutoEmailSelector
                        autoEmail={autoEmail}
                        setAutoEmail={setAutoEmail}
                        selectedTemplates={selectedTemplates}
                        setSelectedTemplates={setSelectedTemplates}
                    />
                    <SubmitCancelButtons
                        onSubmit={handleSubmit(onSubmit)}
                        onCancel={onClose}
                        loading={loading}
                    />
                </Form>
            </Modal.Content>
        </Modal>
    )
}

const InviteSchema = yup.object().shape({
    InviteTo: yup
        .string()
        .email('Invalid email format')
        .required('Email is required'),
    FullName: yup.string().required('Full Name is required'),
    RoleId: yup.number().required('Role is required').nullable(),
    ShowPhotoGps: yup.boolean(),
})

export const AddInviteModal = ({ open, onClose, loading }) => {
    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(InviteSchema),
        defaultValues: { ShowPhotoGps: true },
    })
    const [selectedCustomers, setSelectedCustomers] = useState([])
    const [selectedTemplates, setSelectedTemplates] = useState([])
    const [showCustomerModal, setShowCustomerModal] = useState(false)
    const [showTemplateModal, setShowTemplateModal] = useState(false)
    const [roleOptions, setRoleOptions] = useState([])
    const [loadingRoles, setLoadingRoles] = useState(false)

    // Redesigned state for permission model
    const [accessMode, setAccessMode] = useState('Inherit')
    const [userPermissionOverrides, setUserPermissionOverrides] = useState([])
    const [menuList, setMenuList] = useState([])
    const [loadingMenu, setLoadingMenu] = useState(false)
    const [companyPermissions, setCompanyPermissions] = useState([])
    const [loadingCompanyPermissions, setLoadingCompanyPermissions] = useState(false)

    const { user: currentUser } = useAuth()
    const companyId = currentUser?.CompanyId || currentUser?.Companies?.[0]?.CompanyId
    const selectedRoleId = watch('RoleId')

    useEffect(() => {
        if (open) {
            reset({ ShowPhotoGps: true })
            setAccessMode('Inherit')
            setUserPermissionOverrides([])
            setSelectedCustomers([])
            setSelectedTemplates([])
            fetchRoleTypes()

            // Fetch menu list
            setLoadingMenu(true)
            MenuSettingService.list({ onlyActive: true, onlyVisible: true })
                .then(res => {
                    const sorted = Array.isArray(res) ? res.sort((a, b) => a.MenuId - b.MenuId) : []
                    setMenuList(sorted)
                })
                .catch(err => console.error("Error fetching menus", err))
                .finally(() => setLoadingMenu(false))

            // Fetch company permissions
            if (companyId) {
                setLoadingCompanyPermissions(true)
                PermissionSettingService.getCompanyPermissions(companyId)
                    .then(res => {
                        const perms = Array.isArray(res) ? res : (res?.data || [])
                        setCompanyPermissions(perms)
                    })
                    .catch(err => console.error("Error loading company permissions", err))
                    .finally(() => setLoadingCompanyPermissions(false))
            }
        }
    }, [open, companyId])

    const fetchRoleTypes = async () => {
        try {
            setLoadingRoles(true)
            const response = await GroupService.getUserRoleTypes()
            const formattedOptions = response.map(role => ({
                key: role.RoleId,
                text: role.RoleName,
                value: role.RoleId,
            }))
            setRoleOptions(formattedOptions)
        } catch (error) {
            console.error('Error fetching role types:', error)
        } finally {
            setLoadingRoles(false)
        }
    }

    const hasCompanyPermission = (menuItem) => {
        const requiredKey = `${menuItem.MenuKey}.view`.toLowerCase()
        const directKey = menuItem.MenuKey.toLowerCase()

        let withoutMenuRequiredKey = ''
        let withoutMenuDirectKey = ''
        if (menuItem.MenuKey.toLowerCase().startsWith('menu.')) {
            const stripped = menuItem.MenuKey.slice(5)
            withoutMenuRequiredKey = `${stripped}.view`.toLowerCase()
            withoutMenuDirectKey = stripped.toLowerCase()
        }

        return companyPermissions.some(cp => {
            const pk = cp.PermissionKey?.toLowerCase() || ''
            return (
                pk === requiredKey ||
                pk === directKey ||
                (withoutMenuRequiredKey && pk === withoutMenuRequiredKey) ||
                (withoutMenuDirectKey && pk === withoutMenuDirectKey)
            )
        })
    }

    const getMenuState = (menuItem) => {
        const isRoot = menuItem.ParentMenuId === null
        const rootMenu = isRoot ? menuItem : menuList.find(m => m.MenuId === menuItem.ParentMenuId)
        if (!rootMenu || !hasCompanyPermission(rootMenu)) return 'Deny (company)'

        if (!isRoot && !hasCompanyPermission(menuItem)) {
            return 'Deny (company)'
        }

        const requiredKey = `${menuItem.MenuKey}.view`.toLowerCase()
        const directKey = menuItem.MenuKey.toLowerCase()

        let withoutMenuRequiredKey = ''
        let withoutMenuDirectKey = ''
        if (menuItem.MenuKey.toLowerCase().startsWith('menu.')) {
            const stripped = menuItem.MenuKey.slice(5)
            withoutMenuRequiredKey = `${stripped}.view`.toLowerCase()
            withoutMenuDirectKey = stripped.toLowerCase()
        }

        const matchingCompPerms = companyPermissions.filter(cp => {
            const pk = cp.PermissionKey?.toLowerCase() || ''
            return (
                pk === requiredKey ||
                pk === directKey ||
                (withoutMenuRequiredKey && pk === withoutMenuRequiredKey) ||
                (withoutMenuDirectKey && pk === withoutMenuDirectKey)
            )
        })

        const matchingCompPermIds = new Set(matchingCompPerms.map(cp => cp.PermissionId))

        const override = userPermissionOverrides.find(o => matchingCompPermIds.has(o.PermissionId))
        if (override) {
            return override.Action
        }

        return 'Allow'
    }

    const visibleCount = menuList.filter(m => getMenuState(m) === 'Allow').length

    const onSubmit = formData => {
        onClose({
            ...formData,
            AccessMode: accessMode,
            UserPermissionOverrides: userPermissionOverrides,
            CustomerIds:
                selectedCustomers.length > 0
                    ? selectedCustomers.join(';')
                    : null,
            TemplateIds:
                selectedTemplates.length > 0
                    ? selectedTemplates.join(';')
                    : null,
            LoginSource: 1,
        })
    }

    const handleCustomerModalClose = (shouldRefresh, selectedIds) => {
        setShowCustomerModal(false)
        if (shouldRefresh && selectedIds) {
            setSelectedCustomers(selectedIds)
        }
    }

    const handleTemplateModalClose = (shouldRefresh, selectedIds) => {
        setShowTemplateModal(false)
        if (shouldRefresh && selectedIds) {
            setSelectedTemplates(selectedIds)
        }
    }

    const handleAssignCustomer = () => {
        setShowCustomerModal(true)
    }

    const handleAssignTemplate = () => {
        setShowTemplateModal(true)
    }

    const renderMenuPreview = () => {
        if (loadingMenu || loadingCompanyPermissions) {
            return <div style={{ padding: '12px', textAlign: 'center', color: '#64748B' }}>Loading menu preview...</div>
        }
        const filteredMenuList = menuList.filter(item => getMenuState(item) !== 'Deny (company)')
        if (filteredMenuList.length === 0) {
            return <div style={{ padding: '12px', textAlign: 'center', color: '#64748B' }}>No menus available.</div>
        }
        return filteredMenuList.map(item => {
            const isSub = item.ParentMenuId !== null
            const state = getMenuState(item)
            return (
                <MenuRow key={item.MenuId} indent={isSub} denied={state.startsWith('Deny')}>
                    <span>
                        {isSub ? '└ ' : ''}
                        {item.Title}
                    </span>
                    <MenuStateLabel state={state}>{state}</MenuStateLabel>
                </MenuRow>
            )
        })
    }

    const renderOverrides = () => {
        if (loadingCompanyPermissions) {
            return <div style={{ padding: '12px', textAlign: 'center', color: '#64748B' }}>Loading permissions...</div>
        }
        if (companyPermissions.length === 0) {
            return <div style={{ padding: '12px', textAlign: 'center', color: '#64748B' }}>No permissions available for this company.</div>
        }
        return companyPermissions.map(perm => {
            const override = userPermissionOverrides.find(o => o.PermissionId === perm.PermissionId)
            const currentAction = override ? override.Action : 'Inherit'

            const handleActionChange = (action) => {
                if (action === 'Inherit') {
                    setUserPermissionOverrides(prev => prev.filter(o => o.PermissionId !== perm.PermissionId))
                } else {
                    setUserPermissionOverrides(prev => {
                        const filtered = prev.filter(o => o.PermissionId !== perm.PermissionId)
                        return [...filtered, { PermissionId: perm.PermissionId, Action: action }]
                    })
                }
            }

            return (
                <OverrideRow key={perm.PermissionId}>
                    <OverrideInfo>
                        <OverrideName>{perm.PermissionName}</OverrideName>
                        <OverrideKey>{perm.PermissionKey}</OverrideKey>
                    </OverrideInfo>
                    <PillRadioContainer>
                        <PillButton
                            type="button"
                            active={currentAction === 'Inherit'}
                            onClick={() => handleActionChange('Inherit')}
                        >
                            Inherit
                        </PillButton>
                        <PillButton
                            type="button"
                            actionType="Allow"
                            active={currentAction === 'Allow'}
                            onClick={() => handleActionChange('Allow')}
                        >
                            Allow
                        </PillButton>
                        <PillButton
                            type="button"
                            actionType="Deny"
                            active={currentAction === 'Deny'}
                            onClick={() => handleActionChange('Deny')}
                        >
                            Deny
                        </PillButton>
                    </PillRadioContainer>
                </OverrideRow>
            )
        })
    }

    return (
        <Modal
            open={open}
            onClose={() => onClose()}
            className="model"
            size="small"
            style={{ borderRadius: '8px', overflow: 'hidden' }}
        >
            <ModalHeaderGradient>
                <ModalTitleText>Invite Team Member</ModalTitleText>
                <X
                    className="pointer"
                    size={24}
                    onClick={() => onClose()}
                    style={{ color: '#ffffff' }}
                />
            </ModalHeaderGradient>
            <Modal.Content style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <Form noValidate>
                    <Form.Field>
                        <FormTextFormField
                            control={control}
                            label="Email"
                            name="InviteTo"
                            placeholder="Email"
                            errors={errors?.InviteTo}
                            defaultValue=""
                            required
                        />
                    </Form.Field>
                    <Form.Field>
                        <FormTextFormField
                            control={control}
                            label="Full Name"
                            name="FullName"
                            placeholder="FullName"
                            errors={errors?.FullName}
                            defaultValue=""
                            required
                        />
                    </Form.Field>
                    <Form.Field>
                        <FormDropdownField
                            control={control}
                            label="Role"
                            name="RoleId"
                            placeholder="Select Role"
                            errors={errors?.RoleId}
                            options={roleOptions}
                            loading={loadingRoles}
                            required
                        />
                    </Form.Field>
                    <Form.Field>
                        <FormToggleFormField
                            control={control}
                            name="ShowPhotoGps"
                            label="Show GPS Caption in PDF"
                            defaultValue
                        />
                    </Form.Field>

                    <Form.Field>
                        <label style={{ fontWeight: '600', fontSize: '13.5px', color: '#0F172A', marginBottom: '8px', display: 'block' }}>Menu Access</label>
                        <AccessModesContainer>
                            <AccessModeCard
                                active={accessMode === 'Inherit'}
                                onClick={() => {
                                    setAccessMode('Inherit')
                                    setUserPermissionOverrides([])
                                }}
                            >
                                <AccessModeTitle active={accessMode === 'Inherit'}>
                                    <span style={{ fontSize: '16px', color: accessMode === 'Inherit' ? '#4F46E5' : '#CBD5E1' }}>
                                        {accessMode === 'Inherit' ? '●' : '○'}
                                    </span>
                                    Inherit role defaults
                                </AccessModeTitle>
                                <AccessModeDesc>
                                    No per-user overrides. The user sees whatever the selected role allows after company-level filters.
                                </AccessModeDesc>
                            </AccessModeCard>
                            <AccessModeCard
                                active={accessMode === 'Customize'}
                                onClick={() => setAccessMode('Customize')}
                            >
                                <AccessModeTitle active={accessMode === 'Customize'}>
                                    <span style={{ fontSize: '16px', color: accessMode === 'Customize' ? '#4F46E5' : '#CBD5E1' }}>
                                        {accessMode === 'Customize' ? '●' : '○'}
                                    </span>
                                    Customize
                                </AccessModeTitle>
                                <AccessModeDesc>
                                    Start from role defaults and add per-user Allow/Deny overrides.
                                </AccessModeDesc>
                            </AccessModeCard>
                        </AccessModesContainer>
                    </Form.Field>

                    <Form.Field>
                        <MenuPreviewContainer>
                            <MenuPreviewHead>
                                <span>Effective menus for role "{roleOptions.find(r => r.value === selectedRoleId)?.text || 'Selected Role'}" in this company</span>
                                <MenuPreviewBadge>{visibleCount} menus visible</MenuPreviewBadge>
                            </MenuPreviewHead>
                            <MenuTreeContainer>
                                {renderMenuPreview()}
                            </MenuTreeContainer>
                        </MenuPreviewContainer>
                    </Form.Field>

                    {accessMode === 'Customize' && (
                        <Form.Field>
                            <OverridesContainer>
                                <label style={{ fontWeight: '600', fontSize: '13.5px', color: '#0F172A', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    Per-user overrides
                                    <span style={{ fontSize: '11px', fontWeight: '400', color: '#64748B' }}>
                                        visible only when "Customize" is selected
                                    </span>
                                </label>
                                <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#FFFFFF', maxHeight: '280px', overflowY: 'auto', padding: '0 12px' }}>
                                    {renderOverrides()}
                                </div>
                            </OverridesContainer>
                        </Form.Field>
                    )}

                    <Form.Field style={{ marginTop: '20px' }}>
                        <div className="flex gap-2 mt-2" style={{ display: 'flex', gap: '8px' }}>
                            <Button primary type="button" onClick={handleAssignCustomer}>
                                Assign Customer
                            </Button>
                            <Button primary type="button" onClick={handleAssignTemplate}>
                                Assign Template
                            </Button>
                        </div>
                        {selectedCustomers.length > 0 && (
                            <div className="mt-2" style={{ marginTop: '8px' }}>
                                <strong>Selected Customers:</strong>{' '}
                                {selectedCustomers.length}
                            </div>
                        )}
                        {selectedTemplates.length > 0 && (
                            <div className="mt-2" style={{ marginTop: '8px' }}>
                                <strong>Selected Templates:</strong>{' '}
                                {selectedTemplates.length}
                            </div>
                        )}
                    </Form.Field>
                    <SubmitCancelButtons
                        loading={loading}
                        onSubmit={handleSubmit(onSubmit)}
                        onCancel={onClose}
                    />
                </Form>
            </Modal.Content>
            {showCustomerModal && (
                <InviteCustomerAssignModal
                    open={showCustomerModal}
                    onClose={handleCustomerModalClose}
                />
            )}
            {showTemplateModal && (
                <InviteTemplateAssignModal
                    open={showTemplateModal}
                    onClose={handleTemplateModalClose}
                />
            )}
        </Modal>
    )
}
